import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { useLazyQuery } from '@apollo/react-hooks';
import { useHistory } from 'react-router-dom';
import Button from '../Button';
import Input, { PhoneNumberInput } from '../Input';
import Error from '../Error';
import { getErrorMessage } from '../../util/ErrorUtil';
import { formatPhoneNumberToSend } from '../../util/PhoneNumber';
import { CHECK_USER_EXISTS } from '../../graphql/queries';
import colors from '../../styles/colors';
import NewAccountScreenLayout from './NewAccountScreenLayout';

import step1Image from '../../assets/images/new-account/01.jpg';

const FORM_ID = 'new-account-step1-form';
const STORAGE_KEY_STEP1 = 'newAccount_step1';

function getStep1FromStorage() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY_STEP1);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveStep1ToStorage(data) {
  try {
    sessionStorage.setItem(STORAGE_KEY_STEP1, JSON.stringify(data));
  } catch (err) {
    // ignore
  }
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const NameRow = styled.div`
  display: flex;
  gap: 15px;
  width: 100%;
`;

const TOS = styled.div`
  color: ${colors.grey3};
  padding: 16px 0;
  font-size: 1.2rem;
`;

const TOSLink = styled.a`
  color: ${colors.seaGreen};
`;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const USER_EXISTS_MESSAGE = 'A user with this email and phone number already exists. Please enter different credentials or log in and then create a new organization from dashboard.';

export default function AccountAdminInfo({ onSuccess }) {
  const history = useHistory();
  const stored = getStep1FromStorage();

  const [email, setEmail] = useState(stored?.email || '');
  const [firstName, setFirstName] = useState(stored?.firstName || '');
  const [lastName, setLastName] = useState(stored?.lastName || '');
  const [phoneNumber, setPhoneNumber] = useState(stored?.phoneNumber || '');
  const [password, setPassword] = useState(stored?.password || '');
  const [errorMsg, setErrorMsg] = useState('');
  const [emailError, setEmailError] = useState('');
  const [checking, setChecking] = useState(false);

  const pendingSubmitRef = useRef(null);

  const [checkUserExists, { loading: checkLoading }] = useLazyQuery(CHECK_USER_EXISTS, {
    fetchPolicy: 'network-only',
    onCompleted(data) {
      setChecking(false);
      const userExists = data?.checkUserExists === true;
      const userDoesNotExist = data?.checkUserExists === false;
      if (userExists) {
        setErrorMsg(USER_EXISTS_MESSAGE);
        return;
      }
      if (!userDoesNotExist) {
        setErrorMsg('Could not verify credentials.');
        return;
      }
      const pending = pendingSubmitRef.current;
      if (pending) {
        saveStep1ToStorage(pending);
        pendingSubmitRef.current = null;
        if (onSuccess) onSuccess();
        else history.push('/new-account/step/2');
      }
    },
    onError(err) {
      setChecking(false);
      setErrorMsg(getErrorMessage(err) || 'Could not verify credentials.');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (checking || checkLoading) return;
    setErrorMsg('');
    setEmailError('');

    if (!EMAIL_REGEX.test(email.trim())) {
      setEmailError('Please enter a valid email address.');
      return;
    }

    const phoneNumberToSend = formatPhoneNumberToSend(phoneNumber || '');
    pendingSubmitRef.current = {
      email: email.trim(),
      firstName,
      lastName,
      phoneNumber: phoneNumberToSend,
      password,
    };
    setChecking(true);
    checkUserExists({
      variables: {
        email: email.trim().toLowerCase(),
        phoneNumber: phoneNumberToSend,
      },
    });
  };

  const footer = (
    <>
      {null}
      <Button
        margin="0"
        text="Next"
        type="button"
        onClick={() => document.getElementById(FORM_ID)?.requestSubmit()}
        loading={checking || checkLoading}
        width="auto"
      />
    </>
  );

  return (
    <NewAccountScreenLayout
      stepNumber={1}
      screenImageSrc={step1Image}
      title="Create Your Account"
      showSignInLink
      footer={footer}
    >
      <Form id={FORM_ID} onSubmit={handleSubmit}>
        <NameRow>
          <Input
            type="text"
            header="FIRST NAME"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            width="100%"
          />
          <Input
            type="text"
            header="LAST NAME"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            width="100%"
          />
        </NameRow>
        <Input
          type="email"
          header="EMAIL"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError('');
          }}
          required
          width="100%"
          error={emailError}
        />
        <PhoneNumberInput
          type="tel"
          header="PHONE NUMBER"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          required
          width="100%"
        />
        <Input
          type="password"
          header="PASSWORD"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={8}
          width="100%"
        />
        <TOS>
          By registering, you agree to Check This Out&apos;s{' '}
          <TOSLink
            href="https://intercom.help/check-this-out/en/articles/4339634-check-this-out-terms-of-use-and-privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms of Use and Privacy Policy
          </TOSLink>
        </TOS>
        {errorMsg && (
          <Error error={errorMsg} margin="16px 0 0 0" position="relative" />
        )}
      </Form>
    </NewAccountScreenLayout>
  );
}
